/**
 * Page publique d'un compte rendu diffusé (`/crc/consulter/:token`).
 *
 * RP22 — mise en forme lisible (texte mis en page, plus de texte brut), PDF officiel
 *        (celui joint à l'e-mail, figé à la diffusion), filtre « Vos points » pour
 *        l'entreprise destinataire.
 * RP23 (D17-B) — réaction par point (« Je signale un désaccord » + commentaire) et
 *        « J'approuve / Lu, rien à signaler » pour tout le compte rendu. La
 *        contestation globale reste possible ; la validation tacite (8 jours ouvrés)
 *        continue de s'appliquer sans retour.
 */
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, FilePdf, Warning, CheckCircle, XCircle, Clock, ChatCircleText } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { libelleRole, BOUTONS } from '../lib/libelles';
import CodeVerificationDialog, { CODE_HEADER, verificationRequise } from '../components/shared/CodeVerificationDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Statut du destinataire (vocabulaire 09 §3 : Diffusé → Accepté tacite ou exprès · ou Contesté)
const STATUT_LABEL = {
  envoye: { label: 'Envoyé', color: '#6B6B6B' },
  lu: { label: 'Lu', color: '#1E3A5F' },
  valide_tacite: { label: 'Accepté (validation tacite)', color: '#2D6A4F' },
  approuve: { label: 'Accepté', color: '#2D6A4F' },
  conteste: { label: 'Contesté', color: '#8B3A3A' },
};

const MD = {
  h1: ({ children }) => <h2 className="text-xl text-[#1E3A5F] mt-6 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>{children}</h2>,
  h2: ({ children }) => <h3 className="text-lg font-semibold text-[#1E3A5F] mt-5 mb-2">{children}</h3>,
  h3: ({ children }) => <h4 className="text-base font-semibold text-[#1E3A5F] mt-4 mb-1">{children}</h4>,
  h4: ({ children }) => <h5 className="font-semibold text-[#2C2C2C] mt-3 mb-1">{children}</h5>,
  p: ({ children }) => <p className="mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-6 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-6 mb-3 space-y-1">{children}</ol>,
  table: ({ children }) => (
    <div className="overflow-x-auto mb-4"><table className="w-full text-xs border border-[#E0E0DB]">{children}</table></div>
  ),
  th: ({ children }) => <th className="bg-[#F5F5F3] text-left p-2 border border-[#E0E0DB] font-semibold">{children}</th>,
  td: ({ children }) => <td className="p-2 border border-[#E0E0DB] align-top">{children}</td>,
  blockquote: ({ children }) => <blockquote className="border-l-4 border-[#E0E0DB] pl-3 text-[#4A4A4A] mb-3">{children}</blockquote>,
  hr: () => <hr className="my-4 border-[#E0E0DB]" />,
  a: ({ children, href }) => <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#C4773B] underline">{children}</a>,
};

const dateFr = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleDateString('fr-FR');
};

export default function CRCConsulterPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filtre, setFiltre] = useState('tous');
  const [showContest, setShowContest] = useState(false);
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);
  const [desaccordOuvert, setDesaccordOuvert] = useState(null);
  const [commentaire, setCommentaire] = useState('');
  // RP10 (D10 C) — contester engage : code SMS / e-mail
  const [codeOpen, setCodeOpen] = useState(false);

  const charger = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/crc/consulter/${token}`);
      setData(res.data);
      return res.data;
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien invalide');
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const d = await charger();
      if (d?.nb_vos_points > 0) setFiltre('vos');
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const approuver = async () => {
    try {
      setBusy(true);
      await axios.post(`${API_URL}/api/crc/consulter/${token}/approuver`, {});
      toast.success('Merci, votre accord est enregistré');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const signalerDesaccord = async (pointId) => {
    if (!commentaire.trim()) return toast.error('Expliquez votre désaccord');
    try {
      setBusy(true);
      await axios.post(`${API_URL}/api/crc/consulter/${token}/points/${pointId}/desaccord`, { commentaire });
      toast.success("Désaccord enregistré, l'architecte est prévenu");
      setDesaccordOuvert(null);
      setCommentaire('');
      await charger();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const contester = async (codeVerification = null) => {
    if (!motif.trim()) return toast.error('Motif requis');
    try {
      setBusy(true);
      await axios.post(`${API_URL}/api/crc/consulter/${token}/contester`, { motif }, {
        withCredentials: true,
        headers: codeVerification ? { [CODE_HEADER]: codeVerification } : {},
      });
      toast.success('Contestation enregistrée');
      setShowContest(false);
      await charger();
    } catch (e) {
      if (verificationRequise(e)) {
        if (codeVerification) throw e; // code refusé : affiché dans la fenêtre du code
        setCodeOpen(true);
        return;
      }
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Wrap>
        <div className="text-center py-20"><Clock size={48} className="mx-auto text-[#C4773B] animate-spin" /><p className="mt-4 text-[#6B6B6B]">Chargement...</p></div>
      </Wrap>
    );
  }
  if (error) {
    return (
      <Wrap>
        <div className="text-center py-12">
          <XCircle size={56} weight="fill" className="mx-auto text-[#8B3A3A] mb-4" />
          <h1 className="text-2xl text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif' }}>Lien invalide</h1>
          <p className="text-[#6B6B6B] mt-2">{error}</p>
        </div>
      </Wrap>
    );
  }

  const dest = data.destinataire || {};
  const st = STATUT_LABEL[dest.statut] || {};
  const limite = data.date_limite ? new Date(data.date_limite) : null;
  const expired = limite && limite < new Date();
  const tacite = dest.statut === 'valide_tacite';
  const approuve = !!data.deja_approuve;
  const conteste = dest.statut === 'conteste' || data.deja_conteste;
  const peutReagir = !!data.peut_reagir;
  const points = data.points || [];
  const nbVos = data.nb_vos_points || 0;
  const pointsAffiches = filtre === 'vos' ? points.filter((p) => p.est_a_vous) : points;
  const pdfHref = data.pdf_url ? `${API_URL}${data.pdf_url}` : null;

  return (
    <Wrap>
      {/* En-tête */}
      <div className="border-b border-[#E0E0DB] pb-5 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={18} weight="bold" className="text-[#C4773B]" />
          <span className="text-xs uppercase tracking-wider text-[#C4773B] font-medium">Compte rendu de chantier</span>
        </div>
        <h1 className="text-2xl sm:text-3xl text-[#1E3A5F] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>{data.titre}</h1>
        {data.chantier_nom && <p className="text-sm text-[#6B6B6B]">Chantier : {data.chantier_nom}{data.chantier_adresse ? ` — ${data.chantier_adresse}` : ''}</p>}
        {data.date_debut_periode && (
          <p className="text-sm text-[#6B6B6B]">Période du {dateFr(data.date_debut_periode)} au {dateFr(data.date_fin_periode)}</p>
        )}
        {data.diffusion_date && <p className="text-sm text-[#6B6B6B]">Diffusé le {dateFr(data.diffusion_date)}</p>}
        {pdfHref && (
          <a
            href={pdfHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 px-3 py-2 border border-[#1E3A5F] text-[#1E3A5F] text-sm hover:bg-[#F5F5F3]"
            data-testid="crc-pdf-link"
          >
            <FilePdf size={16} /> {BOUTONS.telechargerPdf}
          </a>
        )}
      </div>

      {/* Destinataire + statut */}
      <div className="bg-[#F8F7F4] border-l-4 border-[#C4773B] p-4 mb-6">
        <p className="text-xs uppercase tracking-wider text-[#6B6B6B] mb-1">Destinataire</p>
        <p className="font-medium text-[#2C2C2C]">{dest.nom}</p>
        {dest.role && <p className="text-xs text-[#6B6B6B]">{libelleRole(dest.role)}</p>}
        {st.label && <p className="text-xs mt-2" style={{ color: st.color }}>Statut : {st.label}</p>}
      </div>

      {/* Bandeaux */}
      {!conteste && !tacite && !approuve && limite && (
        <div className={`border-l-4 p-4 mb-6 ${expired ? 'bg-[#FFEBEE] border-[#8B3A3A]' : 'bg-[#FFF8E1] border-[#B8860B]'}`}>
          <div className="flex items-start gap-2">
            <Warning size={20} weight="fill" className={expired ? 'text-[#8B3A3A]' : 'text-[#B8860B]'} />
            <div className="text-sm" style={{ color: expired ? '#8B3A3A' : '#6B4A25' }}>
              <p className="font-medium">{expired ? 'Délai dépassé — validation tacite en cours' : 'Validation tacite'}</p>
              <p>
                Sans retour de votre part avant le <strong>{limite.toLocaleDateString('fr-FR')}</strong>, ce compte rendu est considéré comme approuvé conformément aux usages de la profession.
              </p>
            </div>
          </div>
        </div>
      )}
      {tacite && (
        <div className="bg-[#E8F5E9] border-l-4 border-[#2D6A4F] p-4 mb-6 flex items-start gap-2">
          <CheckCircle size={20} weight="fill" className="text-[#2D6A4F]" />
          <p className="text-sm text-[#1B4332]">Ce compte rendu a été <strong>accepté par validation tacite</strong> (délai expiré sans retour).</p>
        </div>
      )}
      {approuve && (
        <div className="bg-[#E8F5E9] border-l-4 border-[#2D6A4F] p-4 mb-6 flex items-start gap-2" data-testid="crc-approuve">
          <CheckCircle size={20} weight="fill" className="text-[#2D6A4F]" />
          <p className="text-sm text-[#1B4332]">Vous avez approuvé ce compte rendu le {dateFr(dest.approbation?.date)}. Merci.</p>
        </div>
      )}
      {conteste && (
        <div className="bg-[#FFEBEE] border-l-4 border-[#8B3A3A] p-4 mb-6 flex items-start gap-2">
          <XCircle size={20} weight="fill" className="text-[#8B3A3A]" />
          <div className="text-sm text-[#8B3A3A]">
            <p className="font-medium">Vous avez signalé un désaccord</p>
            {dest.contestation?.motif && !dest.contestation?.par_point && <p className="mt-1 italic">« {dest.contestation.motif} »</p>}
          </div>
        </div>
      )}

      {/* Réponse globale */}
      {peutReagir && !conteste && (
        <div className="mb-8 border border-[#E0E0DB] p-4" data-testid="crc-reponse-globale">
          <p className="text-sm text-[#2C2C2C] mb-3">Rien à signaler ? Dites-le à l'architecte en un clic.</p>
          <Button onClick={approuver} disabled={busy} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-none gap-2" data-testid="approuver-btn">
            <CheckCircle size={16} /> {BOUTONS.jApprouve} / Lu, rien à signaler
          </Button>
          <p className="text-xs text-[#6B6B6B] mt-3">Un point ne vous convient pas ? Utilisez « {BOUTONS.desaccord} » sur ce point, plus bas.</p>
        </div>
      )}

      {/* Points du compte rendu */}
      {points.length > 0 && (
        <div className="mb-8" data-testid="crc-points">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-sm uppercase tracking-wider text-[#6B6B6B]">Actions du compte rendu</h2>
            {nbVos > 0 && (
              <div className="flex text-xs border border-[#E0E0DB]" role="tablist">
                <button
                  onClick={() => setFiltre('vos')}
                  className={`px-3 py-1.5 ${filtre === 'vos' ? 'bg-[#1E3A5F] text-white' : 'text-[#2C2C2C]'}`}
                  data-testid="filtre-vos-points"
                >
                  Vos points ({nbVos})
                </button>
                <button
                  onClick={() => setFiltre('tous')}
                  className={`px-3 py-1.5 ${filtre === 'tous' ? 'bg-[#1E3A5F] text-white' : 'text-[#2C2C2C]'}`}
                  data-testid="filtre-tous-points"
                >
                  Tous les points ({points.length})
                </button>
              </div>
            )}
          </div>
          <ul className="space-y-2">
            {pointsAffiches.map((p) => (
              <li key={p.id} className={`border p-3 ${p.est_a_vous ? 'border-[#C4773B] bg-[#FFFBF7]' : 'border-[#E0E0DB]'}`} data-testid={`crc-point-${p.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#2C2C2C]">
                      {p.numero ? <strong className="mr-1">N° {p.numero}</strong> : null}
                      {p.description}
                    </p>
                    <p className="text-xs text-[#6B6B6B] mt-1">
                      {[p.lot && `Lot ${p.lot}`, p.entreprise, p.echeance ? `Échéance : ${dateFr(p.echeance)}` : null, p.statut_libelle].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  {peutReagir && !p.ma_reaction && desaccordOuvert !== p.id && (
                    <button
                      onClick={() => { setDesaccordOuvert(p.id); setCommentaire(''); }}
                      className="text-xs text-[#8B3A3A] border border-[#8B3A3A] px-2 py-1 hover:bg-[#FFEBEE] flex items-center gap-1"
                      data-testid={`desaccord-btn-${p.id}`}
                    >
                      <ChatCircleText size={14} /> {BOUTONS.desaccord}
                    </button>
                  )}
                </div>
                {p.ma_reaction && (
                  <p className="text-xs text-[#8B3A3A] mt-2 italic">Votre désaccord ({dateFr(p.ma_reaction.date)}) : « {p.ma_reaction.commentaire} »</p>
                )}
                {desaccordOuvert === p.id && (
                  <div className="mt-2 border border-[#8B3A3A] bg-[#FFEBEE] p-3">
                    <textarea
                      rows={3}
                      value={commentaire}
                      onChange={(e) => setCommentaire(e.target.value)}
                      className="w-full border border-[#E0E0DB] p-2 text-sm"
                      placeholder="Expliquez ce qui ne vous convient pas sur ce point…"
                      data-testid={`desaccord-commentaire-${p.id}`}
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <Button variant="outline" size="sm" className="rounded-none" onClick={() => setDesaccordOuvert(null)}>Annuler</Button>
                      <Button size="sm" disabled={busy} onClick={() => signalerDesaccord(p.id)} className="bg-[#8B3A3A] hover:bg-[#6A2A2A] text-white rounded-none" data-testid={`desaccord-envoyer-${p.id}`}>
                        {busy ? 'Envoi…' : 'Envoyer'}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            ))}
            {pointsAffiches.length === 0 && <li className="text-sm text-[#6B6B6B] italic">Aucun point.</li>}
          </ul>
        </div>
      )}

      {/* Contenu mis en forme */}
      <div className="mb-8">
        <h2 className="text-sm uppercase tracking-wider text-[#6B6B6B] mb-3">Compte rendu</h2>
        <div className="bg-white border border-[#E0E0DB] p-4 sm:p-6 text-sm text-[#2C2C2C]" data-testid="crc-contenu">
          {data.contenu
            ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD}>{data.contenu}</ReactMarkdown>
            : <span className="italic text-[#6B6B6B]">Contenu non disponible.</span>}
        </div>
      </div>

      {/* Contestation de l'ensemble (chemin historique, toujours accepté) */}
      {peutReagir && !conteste && (
        <div className="mt-6">
          {!showContest ? (
            <button onClick={() => setShowContest(true)} className="text-xs text-[#8B3A3A] underline" data-testid="open-contest-btn">
              Contester l'ensemble du compte rendu
            </button>
          ) : (
            <div className="border border-[#8B3A3A] bg-[#FFEBEE] p-4">
              <p className="text-sm font-medium text-[#8B3A3A] mb-2">Motif de la contestation</p>
              <textarea
                rows={4}
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                className="w-full border border-[#E0E0DB] p-2 text-sm"
                placeholder="Expliquez précisément ce que vous contestez..."
                data-testid="contest-motif"
              />
              <div className="flex justify-end gap-2 mt-2">
                <Button variant="outline" onClick={() => setShowContest(false)} size="sm" className="rounded-none">Annuler</Button>
                <Button onClick={() => contester()} disabled={busy} size="sm" className="bg-[#8B3A3A] hover:bg-[#6A2A2A] text-white rounded-none" data-testid="confirm-contest-btn">
                  {busy ? 'Envoi...' : 'Envoyer la contestation'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <CodeVerificationDialog
        open={codeOpen}
        onOpenChange={setCodeOpen}
        demanderUrl={`${API_URL}/api/crc/consulter/${token}/code`}
        titre="Confirmer la contestation"
        onValider={(code) => contester(code)}
      />

      <div className="mt-10 pt-6 border-t border-[#E0E0DB] text-[11px] text-[#6B6B6B] leading-relaxed">
        Cette consultation est enregistrée (date, adresse IP, identifiant du destinataire). Le PDF et le texte ci-dessus sont ceux figés au moment de la diffusion. En l'absence de désaccord motivé dans le délai, ce compte rendu est considéré comme approuvé conformément aux usages de la maîtrise d'œuvre.
      </div>
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="min-h-screen bg-[#FAFAF8] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6 text-center">
          <div className="inline-block text-2xl text-[#1E3A5F]" style={{ fontFamily: 'DM Serif Display, serif', letterSpacing: '0.1em' }}>KROQUIS</div>
          <div className="text-[10px] uppercase tracking-widest text-[#6B6B6B] mt-1">Compte rendu de chantier</div>
        </div>
        <div className="bg-white border border-[#E0E0DB] p-5 sm:p-8 shadow-sm">{children}</div>
        <p className="text-center text-[10px] text-[#6B6B6B] mt-4">Kroquis</p>
      </div>
    </div>
  );
}
